import styled from 'styled-components';

import {
   EzwmAccountBlockedFault,
   EzwmAccountLoginFault,
   EzwmPasswordExpiredFault,
} from '@dreryk/egabinet-ezwm-frontend-api';

import { TranslatableText, Translate } from '@dreryk/react-common';
import {
   ButtonVariant,
   fonts,
   Modal,
   ModalCancelButton,
   ModalCloseButton,
   ModalConfirmButton,
   ModalWidth,
   StyledFlex,
   useHistoryPushWithBackAction,
} from '@dreryk/react-components';

import { getNfzLoginPath } from '~legacy.personnel';

import { medicalDevicesTranslationKeys } from '../translations';

import { MedicalDeviceOrderAllFieldsFormBean, MedicalDeviceOrderFormMethods } from './medicalDeviceOrder.types';
import { MedicalDeviceOrderModalPrintButton } from './MedicalDeviceOrderModalPrintButton';

export type MedicalDeviceOrderEzwmAuthenticationFault =
   | EzwmAccountLoginFault
   | EzwmAccountBlockedFault
   | EzwmPasswordExpiredFault;

type WithSubmit = MedicalDeviceOrderFormMethods<MedicalDeviceOrderAllFieldsFormBean, 'handleSubmit'> & {
   printableWithoutVerification: true;
   onSuccess: VoidFunction;
};
type WithoutSubmit = {
   printableWithoutVerification: false;
   handleSubmit?: never;
   onSuccess?: never;
};

type MedicalDeviceOrderVerificationErrorsModalProps = {
   readonly fault: MedicalDeviceOrderEzwmAuthenticationFault;
   readonly onClose: VoidFunction;
} & (WithSubmit | WithoutSubmit);

const resolveErrorMessage = (fault?: MedicalDeviceOrderEzwmAuthenticationFault): TranslatableText => {
   if (fault?.class === 'egabinet-ezwm-frontend-api:EzwmAccountLoginFault') {
      return medicalDevicesTranslationKeys.connectionWithNfzFailedLoginInformationIncorrect;
   }
   if (fault?.class === 'egabinet-ezwm-frontend-api:EzwmPasswordExpiredFault') {
      return medicalDevicesTranslationKeys.nfzPasswordHasExpiredYouNeedToMakePasswordChange;
   }
   return medicalDevicesTranslationKeys.ezwmAccountBlocked;
};

const StyledIdentifiedErrors = styled(Translate)`
   font-weight: ${fonts.fontWeightBold};
`;
StyledIdentifiedErrors.displayName = 'StyledIdentifiedErrors';

export const MedicalDeviceOrderEzwmLoginErrorsModal = (
   props: MedicalDeviceOrderVerificationErrorsModalProps
): JSX.Element => {
   const push = useHistoryPushWithBackAction(true);
   const goToNfzLogin = (): void => {
      push(getNfzLoginPath(), { label: medicalDevicesTranslationKeys.goBackToVisit });
   };
   return (
      <Modal
         testId={'medicalDeviceOrderAuthenticationError'}
         width={ModalWidth.WIDE}
         title={medicalDevicesTranslationKeys.connectionWithNfzFailedLoginInformationIncorrect}
         visible
         animateVisibility
      >
         <StyledFlex direction={'column'}>
            <StyledIdentifiedErrors>{medicalDevicesTranslationKeys.identifiedError}</StyledIdentifiedErrors>
            <span>
               <Translate>{resolveErrorMessage(props.fault)}</Translate>
            </span>
         </StyledFlex>
         <ModalCancelButton onClick={props.onClose} />
         <ModalCloseButton onClick={props.onClose} />
         <ModalConfirmButton
            variant={props.printableWithoutVerification ? ButtonVariant.SECONDARY : ButtonVariant.PRIMARY}
            onClick={goToNfzLogin}
            label={medicalDevicesTranslationKeys.goToNfzLogin}
         />
         {props.printableWithoutVerification ? (
            <MedicalDeviceOrderModalPrintButton
               handleSubmit={props.handleSubmit}
               onClose={props.onClose}
               onPrintSuccess={props.onSuccess}
            />
         ) : null}
      </Modal>
   );
};
